import { Component } from '@angular/core';
import { AuthService, BreadcrumbService, RangeService, StateService } from 'src/app/services';
import { ActivatedRoute, Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { 
  GetPartnershipRequest, GetPartnershipResponse, IdentityWebApiClient, IPartnership, PartnershipStatus, UpdatePartnershipRequest 
} from 'identity-webapi-client';
import { 
  AddAcceptanceRequest, AddAcceptanceResponse, GetTermsAndConditionsRequest, SituViewWebApiClient, DocumentType, IProduct,
  Range
} from 'situview-webapi-client';
import { EncodingService } from 'src/app/services/encoding-service';
import FingerprintJS from '@fingerprintjs/fingerprintjs';
import { ConfigService } from 'src/app/services/config.service';
import { SituBaseComponent } from 'src/app/shared/situ-base-component';

@Component({
  selector: 'partnership',
  templateUrl: './partnership.component.html',
  styleUrls: ['./partnership.component.less']
})

export class PartnershipComponent extends SituBaseComponent {
  id: string = '';
  partnership: IPartnership | null = null;
  partnershipInitalised: boolean = false;

  acceptPartnershipAgreement: boolean = false;
  documentRead: boolean = true;
  latestAgreementUrl: string | undefined;
  latestVersion: string | undefined;
  termsAndConditionsId: string | undefined;
  partnershipStatus = PartnershipStatus;
  partnerProducts: IProduct[] | undefined;

  loadingDecline: boolean = false;
  loadingAccept: boolean = false;

  private _loading = false;
  public get loading(): boolean {
    return this._loading;
  }

  public set loading(val: boolean) {
    this._loading = val;
    this.breadcrumbService.setIsLoading(val);
  }

  constructor(
    authService: AuthService,
    stateService: StateService,
    private route: ActivatedRoute,
    private router: Router,
    private identityWebApiClient: IdentityWebApiClient,
    private snackBar: MatSnackBar,
    private breadcrumbService: BreadcrumbService,
    private situViewWebApiClient: SituViewWebApiClient,
    private configService: ConfigService,
    private rangeService: RangeService) {
      super(authService, stateService);
      this.id = this.route.snapshot.params['partnershipId'];
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.loadPartnership();
  }

  declineAgreement() {
    this.transitionPartnership(PartnershipStatus.Denied, "");
  }

  acceptAgreement() {
    this.loadingAccept = true;
    let request = new AddAcceptanceRequest();
    request.companyId = this.selectedCompany?.id;
    request.resourceId = this.partnership?.id ?? "";
    request.termsAndCondtionsId = this.termsAndConditionsId ?? "";
    this.loading = true;
    this.addAcceptance(request).then((response) => {
        if (response.success) {
          this.transitionPartnership(PartnershipStatus.Approved, response.termsAndConditionsAcceptanceId ?? "")
            .then(x => this.loadPartnership());
          this.snackBar.open(`Legal Agreement has been marked as accepted.`, 'OK', { duration: 2000 });
        } else {
          this.snackBar.open(`An error occured whilst attempting to accept Legal Agreement.`, 'OK', { duration: 2000 });
        }
        this.loading = false;
        this.loadingAccept = false;
    }, error => {
        this.snackBar.open(`An error occured whilst attempting to accept Legal Agreement.`, 'OK', { duration: 2000 });
        this.loading = false;
        this.loadingAccept = false;
    });
  }

  handleDocumentReadEvent(documentRead: boolean) {
    this.documentRead = documentRead;
  }

  private async addAcceptance(request: AddAcceptanceRequest): Promise<AddAcceptanceResponse> {
    let visitorId = await this.getVisitorId();
    let userDetails = this.getUserDetails(visitorId);
    var url = new URL(this.configService.config.situViewWebApiBaseUrl);
    url.pathname = "api/commands/addacceptance";
    return fetch(url.toString(), {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'AcceptanceHeaderInfo': `${EncodingService.encode(userDetails)}`,
            'Authorization': this.authService.getAuthorizationHeaderValue()
        },
        body: JSON.stringify(request),
    })
    .then(res =>  res.json())
    .then(json => json as AddAcceptanceResponse);
  }

  private loadPartnership(): Promise<void | GetPartnershipResponse> {
    let getPartnershipRequest = new GetPartnershipRequest();
    getPartnershipRequest.partnershipId = this.id;
    return this.identityWebApiClient.getPartnership(getPartnershipRequest)
      .then(x => {
        if (x.success) {
          this.partnership = x.partnership ?? null;
          this.breadcrumbService.setParam('companyName', this.getOtherCompanyName(this.partnership));

          switch (x.partnership?.status) {
            case PartnershipStatus.Requested:
              let getTermsAndConditionsRequest = new GetTermsAndConditionsRequest();
              getTermsAndConditionsRequest.documentType = DocumentType.Partnership;
              getTermsAndConditionsRequest.ianaLanguageSubtag = "en";
      
              this.situViewWebApiClient.getTermsAndConditions(getTermsAndConditionsRequest)
                .then(getTermsAndConditionsResponse => {
                    this.latestAgreementUrl = getTermsAndConditionsResponse.documentLink ?? "";
                    this.termsAndConditionsId = getTermsAndConditionsResponse.termsAndConditionsId;
                    let lang = !!(getTermsAndConditionsResponse?.ianaLanguageSubtag) ? ` (${getTermsAndConditionsResponse.ianaLanguageSubtag})` : getTermsAndConditionsResponse.ianaLanguageSubtag ?? "";
                    this.latestVersion = `v${getTermsAndConditionsResponse.major}.${getTermsAndConditionsResponse.minor}.${getTermsAndConditionsResponse.patch}${lang}`;
                }, error => {
                    this.snackBar.open(`Unable to load Legal Agreement.`, 'OK', { duration: 2000 });
                });
              break;
            case PartnershipStatus.Reconnected:
            case PartnershipStatus.Approved:
              this.loading = true;
              this.loadProductRanges();
              break;
            default:
              break;
          }
        } else {
          this.snackBar.open(`Could not fetch the company. Refresh Page.`, 'OK', { duration: 2000 });
        }
      });
  }

  private getOtherCompanyName(partnership: IPartnership | null): string | undefined {
    if (!partnership) return "";
    if (partnership.requesterCompanyId !== this.selectedCompany?.id) {
      return partnership.requesterCompany?.name;
    }
    return partnership.requesteeCompany?.name; 
  }

  getOtherCompanyId(partnership: IPartnership | null): string | undefined {
    if (!partnership) return "";
    if (partnership.requesterCompanyId !== this.selectedCompany?.id) {
      return partnership.requesterCompany?.id;
    }
    return partnership.requesteeCompany?.id; 
  }

  private getUserDetails(visitorId: string) {
    const offset = new Date().getTimezoneOffset();
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    let userDetails = {
        RemoteIpAddress: "",
        UserId: this.authService.getUserId(),
        VisitorId: visitorId,
        TimeZoneOffset: offset,
        TimeZone: timezone,
    };
    return userDetails;
  }

  private async getVisitorId() {
    let visitorId: string | undefined;

    const fp = await FingerprintJS.load();
    const result = await fp.get();
    visitorId = result.visitorId;

    return visitorId;
  }

  rangeFilterId: string | undefined;
  allProductRanges: Range[] = [];
  private loadProductRanges() {
    return this.rangeService.ListSharedRanges(this.selectedCompany?.id!)
      .then((ranges: Range[]) => {
        const otherCompanyId = this.getOtherCompanyId(this.partnership);
        this.allProductRanges = ranges.filter(x => x && x.companyId === otherCompanyId);
        this.rangeFilterId = this.allProductRanges.find(x => x.name === "All")?.id;
        this.partnershipInitalised = true;
      });
  }

  transitionPartnership(newStatus: PartnershipStatus, termsAndConditionsId: string) {
    this.loading = true;
    if (newStatus === PartnershipStatus.Denied) {
      this.loadingDecline = true;
    }
    let request = new UpdatePartnershipRequest();
    request.id = this.partnership?.id;
    request.status = newStatus;
    request.companyId = this.selectedCompany?.id;
    if (termsAndConditionsId) {
      request.termsAndConditionsId = termsAndConditionsId;
    }

    return this.identityWebApiClient.updatePartnership(request)
      .then(response => {
        if (response.success) {
          switch (request.status) {
            case PartnershipStatus.Denied:
            case PartnershipStatus.Cancelled:
            case PartnershipStatus.Terminated:
            case PartnershipStatus.Reconnected:
              this.router.navigate(['/admin/partnerships']);
              break;
          };
        } else {
          this.snackBar.open(`Error: ${response.errorMessages}!`, 'OK', { duration: 2000 });
        }
        this.loadingDecline = false;
        this.loading = false;
      }, error => {
        this.loading = false;
      });
  }
}
